import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { switchMap, catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { 
  faSort, 
  faSortDown, 
  faSortUp,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faEye,
  faCircle
} from '@fortawesome/free-solid-svg-icons';
import { IPagedData, ITreatmentPlanDetailed } from 'src/app/models/treatment-plan.model';
import { TreatmentPlanService } from 'src/app/services/treatment-plan.service';
import { SearchService } from '../../services/search.service';

@Component({
  selector: 'app-treatment-plans-v2',
  templateUrl: './treatment-plans.component.html',
  styleUrls: ['./treatment-plans.component.sass']
})
export class TreatmentPlansV2Component implements OnInit, OnDestroy {
  Math = Math;
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  
  treatmentPlans: ITreatmentPlanDetailed[] = [];
  filterType: 'ACTIVE' | 'CLOSED' | 'ALL' = 'ACTIVE';
  initialDate = '';
  finalDate = '';
  searchTerm = '';

  private searchSubscription?: Subscription;
  private loadSubscription?: Subscription;
  private loadSubject = new Subject<{
    pageNumber: number;
    pageSize: number;
    status?: 'ACTIVE' | 'CLOSED' | 'ALL';
  }>();

  // Icons
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faEye = faEye;
  faCircle = faCircle;

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  constructor(
    private router: Router,
    private treatmentPlanService: TreatmentPlanService,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.setupLoadSubscription();
    this.searchService.setSearchTerm('');
    this.loadTreatmentPlans(0, this.pageSize);

    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      if (this.searchTerm !== term) {
        this.searchTerm = term;
        this.loadTreatmentPlans(0, this.pageSize);
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    this.loadSubject.complete();
  }

  private setupLoadSubscription(): void {
    this.loadSubscription = this.loadSubject
      .pipe(
        switchMap(({ pageNumber, pageSize, status }) => {
          this.loading = true;
          
          const statusParam = status === 'ALL' ? undefined : status;
          const searchTerm = this.searchTerm?.trim() || undefined;
          
          return this.treatmentPlanService.listAllTreatmentPlans(
            pageNumber, 
            pageSize, 
            statusParam, 
            this.initialDate, 
            this.finalDate, 
            undefined, 
            searchTerm
          ).pipe(
            catchError(error => {
              console.error('Erro ao carregar planos de tratamento:', error);
              return of({ 
                data: [], 
                page_info: { current_page: 0, total_pages: 0, total_elements: 0, elements_per_page: 0 } 
              } as IPagedData<ITreatmentPlanDetailed>);
            }),
            finalize(() => {
              this.loading = false;
            })
          );
        })
      )
      .subscribe({
        next: (response: IPagedData<ITreatmentPlanDetailed>) => {
          this.treatmentPlans = response.data.map(plan => {
            if (typeof plan.total_value === 'string') {
              return {
                ...plan,
                total_value: this.parseStringCurrencyToNumber(plan.total_value as unknown as string)
              };
            }
            return plan;
          });
          
          this.currentPage = response.page_info.current_page;
          this.totalPages = response.page_info.total_pages;
          this.totalElements = response.page_info.total_elements;
        },
        error: (error) => {
          console.error('Erro na subscription:', error);
          this.treatmentPlans = [];
          this.loading = false;
        }
      });
  }

  loadTreatmentPlans(pageNumber: number, pageSize: number): void {
    this.loadSubject.next({
      pageNumber,
      pageSize,
      status: this.filterType
    });
  }

  changeFilter(filter: 'ACTIVE' | 'CLOSED' | 'ALL'): void {
    this.filterType = filter;
    this.loadTreatmentPlans(0, this.pageSize);
  }

  onDateChange(): void {
    this.loadTreatmentPlans(0, this.pageSize);
  }

  clearDateFilters(): void {
    this.initialDate = '';
    this.finalDate = '';
    this.loadTreatmentPlans(0, this.pageSize);
  }

  viewTreatmentPlan(id: string): void {
    this.router.navigate(['/main/v2/treatment-plan', id]);
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  formatCurrency(value: number | undefined): string {
    if (value === undefined || value === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  parseStringCurrencyToNumber(currencyString: string): number {
    if (!currencyString) return 0;
    const cleanString = currencyString.replace(/[R$\s]/g, '');
    const normalizedString = cleanString.replace(/\./g, '').replace(',', '.');
    return parseFloat(normalizedString) || 0;
  }

  getStatusClass(status: string | undefined): string {
    return status === 'ACTIVE' ? 'text-emerald-500' : 'text-gray-500';
  }

  getStatusText(status: string | undefined): string {
    return status === 'ACTIVE' ? 'Ativo' : 'Finalizado';
  }

  // Paginação
  changePageSize(event: Event): void {
    this.pageSize = +(<HTMLSelectElement>event.target).value;
    this.loadTreatmentPlans(0, this.pageSize);
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.loadTreatmentPlans(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.loadTreatmentPlans(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage + 1 < this.totalPages) {
      this.loadTreatmentPlans(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage + 1 < this.totalPages) {
      this.loadTreatmentPlans(this.totalPages - 1, this.pageSize);
    }
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.loadTreatmentPlans(+page - 1, this.pageSize);
    }
  }
}
